import type { ExportRow, ExportSchedule } from '@contract';
import { Download01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '@/components/data-table';
import { PageHeader } from '@/components/page-header';
import { Cell, StatusBadge } from '@/components/record-table';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { callAdmin } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { hourLabel } from '@/lib/market';
import { adminQuery, invalidate } from '@/lib/query';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/_app/exports')({
  component: ExportsPage,
});

/** The staff member's own exports. A link expires after a few minutes. */
function ExportsPage() {
  const list = useQuery({
    ...adminQuery('exports.list', {}),
    refetchInterval: (query) =>
      query.state.data?.some((row) => row.status === 'building') ? 5000 : false,
  });
  const download = useMutation({
    mutationFn: (exportId: string) =>
      callAdmin('exports.link', { export_id: exportId }),
    onSuccess: (link) => {
      if (link.url) window.location.assign(link.url);
      else toast.error('The file could not be found.');
    },
    onError: (error) => toast.error(error.message),
  });

  const [repeat, setRepeat] = useState<ExportRow | null>(null);
  const columns = exportColumns(
    (exportId) => download.mutate(exportId),
    download.isPending,
    setRepeat,
  );

  return (
    <>
      <PageHeader
        title="Exports"
        description="Your CSV exports, with every value shown in full. Each download is logged and its link expires after a few minutes."
      />
      {list.isError ? (
        <Alert variant="destructive">{list.error.message}</Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={list.data}
        empty="No exports yet. Use Export CSV on a list."
      />
      <Schedules />
      {repeat ? (
        <ScheduleDialog source={repeat} onClose={() => setRepeat(null)} />
      ) : null}
    </>
  );
}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** Exports that run by themselves; the owner is emailed when each is ready. */
function Schedules() {
  const schedules = useQuery(adminQuery('exports.schedules', {}));
  const unschedule = useMutation({
    mutationFn: (scheduleId: string) =>
      callAdmin('exports.unschedule', { schedule_id: scheduleId }),
    onSuccess: () => invalidate('exports.schedules'),
    onError: (error) => toast.error(error.message),
  });
  const columns = scheduleColumns(
    (scheduleId) => unschedule.mutate(scheduleId),
    unschedule.isPending,
  );
  return (
    <section className="mt-8 flex flex-col gap-2">
      <h2 className="font-semibold">Scheduled</h2>
      <p className="text-sm text-muted-foreground">
        Repeat an export daily or weekly with Repeat on a row above. You get an
        email when each one is ready; the file stays here.
      </p>
      {schedules.isError ? (
        <Alert variant="destructive">{schedules.error.message}</Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={schedules.data}
        empty="Nothing scheduled."
      />
    </section>
  );
}

function ScheduleDialog({
  source,
  onClose,
}: Readonly<{ source: ExportRow; onClose: () => void }>) {
  const [every, setEvery] = useState<'daily' | 'weekly'>('daily');
  const [hour, setHour] = useState(6);
  const [weekday, setWeekday] = useState(1);
  const schedule = useMutation({
    mutationFn: () =>
      callAdmin('exports.schedule', {
        source: source.source,
        args: source.filters,
        every,
        hour_utc: hour,
        weekday: every === 'weekly' ? weekday : undefined,
      }),
    onSuccess: async (row) => {
      await invalidate('exports.schedules');
      toast.success('Scheduled', {
        description: `First run ${formatDateTime(row.next_run_at)}.`,
      });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Repeat {source.title}</DialogTitle>
          <DialogDescription>
            The same list with the same filters, with every value shown in full.
            It stops if your access changes.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Select
            label="How often"
            value={every}
            onChange={(event) =>
              setEvery(event.target.value as 'daily' | 'weekly')
            }
          >
            <option value="daily">Every day</option>
            <option value="weekly">Every week</option>
          </Select>
        </div>
        {every === 'weekly' ? (
          <div className="flex flex-col gap-2">
            <Select
              label="Day"
              value={String(weekday)}
              onChange={(event) => setWeekday(Number(event.target.value))}
            >
              {WEEKDAYS.map((day, index) => (
                <option key={day} value={index}>
                  {day}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        <div className="flex flex-col gap-2">
          <Select
            label={hourLabel()}
            value={String(hour)}
            onChange={(event) => setHour(Number(event.target.value))}
          >
            {HOURS.map((value) => (
              <option key={value} value={value}>
                {String(value).padStart(2, '0')}:00
              </option>
            ))}
          </Select>
        </div>
        {schedule.error ? (
          <Alert variant="destructive">{schedule.error.message}</Alert>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={schedule.isPending}
            onClick={() => schedule.mutate()}
          >
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const scheduleColumn = createColumnHelper<ExportSchedule>();

function scheduleColumns(onStop: (scheduleId: string) => void, busy: boolean) {
  return [
    scheduleColumn.accessor('title', { header: 'List' }),
    scheduleColumn.accessor('every', {
      header: 'When',
      cell: (info) => {
        const row = info.row.original;
        const at = `${String(row.hour_utc).padStart(2, '0')}:00`;
        return row.every === 'weekly'
          ? `${WEEKDAYS[row.weekday ?? 0]}s at ${at}`
          : `Daily at ${at}`;
      },
    }),
    scheduleColumn.accessor('next_run_at', {
      header: 'Next',
      cell: (info) => <Cell kind="datetime" value={info.getValue()} />,
    }),
    scheduleColumn.accessor('last_run_at', {
      header: 'Last',
      cell: (info) => <Cell kind="datetime" value={info.getValue()} />,
    }),
    scheduleColumn.accessor('filters', {
      header: 'Filters',
      cell: (info) => <Cell kind="json" value={info.getValue()} />,
    }),
    scheduleColumn.display({
      id: 'stop',
      header: '',
      cell: (info) => (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => onStop(info.row.original.id)}
        >
          Stop
        </Button>
      ),
    }),
  ];
}

const column = createColumnHelper<ExportRow>();

/** The exports list; the download button asks for a fresh link. */
function exportColumns(
  onDownload: (exportId: string) => void,
  busy: boolean,
  onRepeat: (row: ExportRow) => void,
) {
  return [
    column.accessor('created_at', {
      header: 'Made',
      cell: (info) => <Cell kind="datetime" value={info.getValue()} />,
    }),
    column.accessor('title', { header: 'List' }),
    column.accessor('row_count', {
      header: 'Rows',
      cell: (info) =>
        `${info.getValue()}${info.row.original.truncated ? ' (cut at the limit)' : ''}`,
    }),
    column.accessor('status', {
      header: 'Status',
      cell: (info) => <StatusBadge status={info.getValue()} />,
    }),
    column.accessor('filters', {
      header: 'Filters',
      cell: (info) => <Cell kind="json" value={info.getValue()} />,
    }),
    column.display({
      id: 'download',
      header: '',
      cell: (info) =>
        info.row.original.status === 'ready' ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => onDownload(info.row.original.id)}
            >
              <HugeiconsIcon icon={Download01Icon} size={16} />
              Download
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRepeat(info.row.original)}
            >
              Repeat
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">
            {info.row.original.error ?? ''}
          </span>
        ),
    }),
  ];
}
